import cn from 'classnames'

import { Tooltip } from 'core-components/library/tooltip'

import styles from './CockpitIconButton.module.scss'

interface ICockpitIconButtonProps {
  /** Icon-font classes the button draws, e.g. `kbase kbase-expand`. */
  icon: string
  /** What the button does, said in the tooltip and to a screen reader. */
  label: string
  /** Prints the label beside the icon instead of keeping it in the tooltip, for a control that
   *  stands on its own rather than in a row of chips. */
  showLabel?: boolean
  /** Extra hint for a labelled button, shown by the browser on hover. */
  title?: string
  className?: string
  disabled?: boolean
  /** Set on a button that opens and closes a region, with `ariaControls` naming that region. */
  expanded?: boolean
  ariaControls?: string
  /** Set on a button that switches something on and off rather than revealing a region. */
  pressed?: boolean
  /** Makes the chip a link to this address, e.g. a website or a phone number. A disabled chip is
   *  never a link. */
  href?: string
  /** Where the link opens; `_blank` for a new tab. */
  target?: string
  onClick?: () => void
}

/**
 * One act of a card header, drawn as an icon in the header's own chip. Every control in the row
 * wears this box, so the row reads as one set of controls rather than a handful of loose glyphs,
 * and the icon alone is never the whole message: `label` is both the tooltip and the accessible
 * name.
 */
export const CockpitIconButton = ({
  icon,
  label,
  showLabel = false,
  title,
  className,
  disabled,
  expanded,
  ariaControls,
  pressed,
  href,
  target,
  onClick,
}: ICockpitIconButtonProps) => {
  const chipClassName = cn(styles.root, { [styles.withLabel]: showLabel }, className)
  const content = (
    <>
      {showLabel && <span className={styles.label}>{label}</span>}
      <i className={icon} aria-hidden />
    </>
  )

  const button =
    href && !disabled ? (
      <a
        className={chipClassName}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        aria-label={label}
        title={title}
        onClick={onClick}
      >
        {content}
      </a>
    ) : (
      <button
        type="button"
        className={chipClassName}
        aria-label={label}
        title={title}
        aria-controls={ariaControls}
        aria-expanded={expanded}
        aria-pressed={pressed}
        disabled={disabled}
        onClick={onClick}
      >
        {content}
      </button>
    )

  /* A printed label is the tooltip's own text, so the tooltip would say it twice. */
  return showLabel ? (
    button
  ) : (
    <Tooltip inline tipContent={() => <span>{label}</span>}>
      {button}
    </Tooltip>
  )
}

CockpitIconButton.displayName = 'CockpitIconButton'
